import React, { useEffect, useState } from 'react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Button, Input, notification } from 'antd';
import { useCurrency } from '../../context/CurrencyContext';
import { useCurrencyConverter } from '../../context/currencyHook';
import { getDiscountCodeDetailByCode } from '../../api/discount.service';
import config from '../../config';

const CheckoutForm = ({
  handleSuccess,
  errorBooking,
  retryBooking,
  amount,
  setDiscountedAmount,
  discountedAmount,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [clientSecret, setClientSecret] = useState('');
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountPercent, setDiscountPercent] = useState(null);

  const convertCurrency = useCurrencyConverter();
  const { currency } = useCurrency();

  const createPaymentIntent = async () => {
    try {
      // Amount and discountedAmount are always in PKR from Summary component
      // We need to convert to selected currency before creating payment intent
      const originalAmountPKR = amount || 0;
      const discountedAmountPKR = discountedAmount !== null && discountedAmount !== undefined ? discountedAmount : null;
      
      // Convert to selected currency for payment
      const originalAmountInCurrency = currency.toLowerCase() === 'pkr' 
        ? originalAmountPKR 
        : convertCurrency(originalAmountPKR);
      
      const finalAmountInCurrency = discountedAmountPKR !== null
        ? (currency.toLowerCase() === 'pkr' ? discountedAmountPKR : convertCurrency(discountedAmountPKR))
        : originalAmountInCurrency;
      
      let amountInSmallestUnit;

      if (currency.toLowerCase() === 'pkr') {
        // For PKR, convert to USD first, then to cents
        const amountInUSD = convertCurrency(parseFloat(finalAmountInCurrency) || 0);
        amountInSmallestUnit = Math.round(amountInUSD * 100);
      } else {
        // For GBP, USD, EUR, etc., amount is already converted, just convert to smallest unit (pence, cents, etc.)
        const baseAmount = parseFloat(finalAmountInCurrency) || 0;
        amountInSmallestUnit = Math.round(baseAmount * 100);
      }

      const payload = {
        amount: amountInSmallestUnit,
        currency: currency.toLowerCase(),
      };
      
      const response = await fetch(`${config.API_URL}/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setClientSecret(data.clientSecret);
      } else {
        throw new Error(data.error || 'Failed to create payment intent');
      }
    } catch (error) {
      console.error('Error creating payment intent:', error);
      setMessage('Failed to initialize payment. Please try again.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setMessage('Stripe has not loaded properly. Please refresh the page.');
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
        },
      });

      if (error) {
        setMessage(`Payment failed: ${error.message}`);
      } else if (paymentIntent.status === 'succeeded') {
        setMessage('Payment successful!');
        handleSuccess(discountCode);
      }
    } catch (error) {
      console.error('Error during payment confirmation:', error);
      setMessage('An error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApplyDiscountOnAmount = (discountPercent) => {
    // Discount is always applied to PKR amount (amount is in PKR)
    const discounted = amount - amount * (discountPercent / 100);
    setDiscountedAmount(discounted);
  };

  const handleApplyDiscountCode = async () => {
    try {
      const data = await getDiscountCodeDetailByCode(discountCode);
      handleApplyDiscountOnAmount(data?.data?.discountPercent);
      setDiscountPercent(data?.data?.discountPercent);
      notification.success({ message: 'Discount code applied' });
    } catch (error) {
      notification.error({ message: error?.response?.data?.message || 'Failed to fetch' });
    }
  };

  useEffect(() => {
    // Re-create payment intent when currency, amount, or discount changes
    // This ensures the payment intent matches the selected currency
    if (currency) {
      createPaymentIntent();
    }
  }, [discountedAmount, amount, currency]);

  return (
    <div className="max-w-md mx-auto p-6 rounded-2xl shadow-lg bg-white space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Checkout</h2>
        <p
          className={`text-lg font-semibold ${
            discountedAmount ? 'line-through text-red-500' : 'text-gray-700'
          }`}
        >
          Total Amount: {currency} {convertCurrency(amount)}
        </p>
        {discountPercent && (
          <p className="text-green-600 font-semibold">Discount: {discountPercent}%</p>
        )}
        {discountedAmount && (
          <p className="text-lg font-semibold text-gray-800">
            Discounted Amount: {currency} {convertCurrency(discountedAmount)}
          </p>
        )}
      </div>

      <div className="space-y-3">
        <Input
          placeholder="Enter Discount Code"
          onChange={(e) => setDiscountCode(e.target.value)}
          className="rounded-md border-gray-300"
        />
        <Button type="primary" block onClick={handleApplyDiscountCode} style={{marginTop: '15px'}}>
          Apply Discount Code
        </Button>
      </div>

      {clientSecret && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Card Number</label>
            <div className="border border-gray-300 p-2 rounded-md">
              <CardNumberElement />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="w-1/2">
              <label className="block text-sm font-medium mb-1">Expiry Date</label>
              <div className="border border-gray-300 p-2 rounded-md">
                <CardExpiryElement />
              </div>
            </div>
            <div className="w-1/2">
              <label className="block text-sm font-medium mb-1">CVC</label>
              <div className="border border-gray-300 p-2 rounded-md">
                <CardCvcElement />
              </div>
            </div>
          </div>

          <Button
            type="primary"
            htmlType="submit"
            block
            loading={isProcessing}
            disabled={!stripe || isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Pay Now'}
          </Button>

          {errorBooking && (
            <Button block onClick={retryBooking}>
              Retry
            </Button>
          )}
        </form>
      )}

      {message && <p className={`text-center ${message.toLowerCase().includes('success') ? 'text-green-600' : 'text-red-600'}`}>{message}</p>}
    </div>
  );
};

export default CheckoutForm;